import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { DensityProvider } from '@/components/admin/context/DensityContext';

import AdminSidebar from '@/components/admin/components/AdminSidebar';
import AdminTopbar from '@/components/admin/components/AdminTopbar';
import CommandPalette, { type PaletteItem } from '@/components/shared/CommandPalette';
import KeyboardHelpOverlay from '@/components/shared/KeyboardHelpOverlay';
import { useKeyboardNav, type GotoShortcut } from '@/hooks/use-keyboard-nav';
import {
  LayoutDashboard, BookOpen, Users, FileText, Package, BarChart3,
  Bell, Settings, LayoutTemplate, ShieldAlert, ServerCog, Tag, Sparkles,
  Scale, Mail, Star,
} from 'lucide-react';

import OverviewModule from '@/components/admin/modules/OverviewModule';
import BooksModule from '@/components/admin/modules/BooksModule';
import UsersModule from '@/components/admin/modules/UsersModule';
import InventoryModule from '@/components/admin/modules/InventoryModule';
import ReportsModule from '@/components/admin/modules/ReportsModule';
import NotificationsModule from '@/components/admin/modules/NotificationsModule';
import SettingsModule from '@/components/admin/modules/SettingsModule';
import SiteContentModule from '@/components/admin/modules/SiteContentModule';
import SecurityModule from '@/components/admin/modules/SecurityModule';
import SystemModule from '@/components/admin/modules/SystemModule';
import ConditionsModule from '@/components/admin/modules/ConditionsModule';
import PoliciesModule from '@/components/admin/modules/PoliciesModule';
import OverdueNoticesModule from '@/components/admin/modules/OverdueNoticesModule';
import ReviewsModule from '@/components/admin/modules/ReviewsModule';

const SIDEBAR_COLLAPSED = 48;
const SIDEBAR_EXPANDED = 220;

const ADMIN_PALETTE_ITEMS: PaletteItem[] = [
  { label: 'Overview',        href: '/admin/overview',         icon: LayoutDashboard, shortcut: 'G O', group: 'Admin' },
  { label: 'Books',           href: '/admin/books',            icon: BookOpen,        shortcut: 'G B', group: 'Admin' },
  { label: 'Genres',          href: '/admin/genres',           icon: Tag,                              group: 'Admin' },
  { label: 'Reviews',         href: '/admin/reviews',          icon: Star,                             group: 'Admin' },
  { label: 'Users',           href: '/admin/users',            icon: Users,           shortcut: 'G U', group: 'Admin' },
  { label: 'Loans',           href: '/admin/loans',            icon: FileText,        shortcut: 'G L', group: 'Admin' },
  { label: 'Policies',        href: '/admin/policies',         icon: Scale,                            group: 'Admin' },
  { label: 'Overdue Notices', href: '/admin/overdue-notices',  icon: Mail,                             group: 'Admin' },
  { label: 'Inventory',       href: '/admin/inventory',        icon: Package,         shortcut: 'G I', group: 'Admin' },
  { label: 'Conditions',      href: '/admin/conditions',       icon: Sparkles,                         group: 'Admin' },
  { label: 'Reports',         href: '/admin/reports',          icon: BarChart3,       shortcut: 'G R', group: 'Admin' },
  { label: 'Notifications',   href: '/admin/notifications',    icon: Bell,            shortcut: 'G N', group: 'Admin' },
  { label: 'Security',        href: '/admin/security',         icon: ShieldAlert,                      group: 'Admin' },
  { label: 'System',          href: '/admin/system',           icon: ServerCog,                        group: 'Admin' },
  { label: 'Settings',        href: '/admin/settings',         icon: Settings,        shortcut: 'G S', group: 'Admin' },
  { label: 'Site Content',    href: '/admin/content',          icon: LayoutTemplate,                   group: 'Admin' },
];

const ADMIN_GOTO: GotoShortcut[] = [
  { key: 'O', label: 'Overview',      href: '/admin/overview'      },
  { key: 'B', label: 'Books',         href: '/admin/books'         },
  { key: 'U', label: 'Users',         href: '/admin/users'         },
  { key: 'L', label: 'Loans',         href: '/admin/loans'         },
  { key: 'I', label: 'Inventory',     href: '/admin/inventory'     },
  { key: 'R', label: 'Reports',       href: '/admin/reports'       },
  { key: 'N', label: 'Notifications', href: '/admin/notifications' },
  { key: 'S', label: 'Settings',      href: '/admin/settings'      },
];

const AdminDashboard: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const [pinned, setPinned] = useState(false);
  const { helpOpen, closeHelp } = useKeyboardNav({ shortcuts: ADMIN_GOTO });

  const sidebarWidth = pinned || expanded ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED;

  return (
    <DensityProvider>
      <CommandPalette items={ADMIN_PALETTE_ITEMS} variant="admin" />
      {helpOpen && (
        <KeyboardHelpOverlay shortcuts={ADMIN_GOTO} onClose={closeHelp} variant="admin" />
      )}
      <div className="min-h-screen bg-[#F8F7F5] flex">
        <AdminSidebar
          expanded={pinned || expanded}
          pinned={pinned}
          onMouseEnter={() => !pinned && setExpanded(true)}
          onMouseLeave={() => !pinned && setExpanded(false)}
          onPinToggle={() => setPinned(p => !p)}
        />
        <div
          className="flex-1 flex flex-col min-w-0"
          style={{ marginLeft: sidebarWidth, transition: 'margin-left 200ms ease' }}
        >
          <AdminTopbar />
          <main className="flex-1 p-6" style={{ paddingTop: 'calc(64px + 24px)' }}>
            <Routes>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<OverviewModule />} />
              <Route path="books" element={<BooksModule />} />
              <Route path="users" element={<UsersModule />} />
              <Route path="inventory" element={<InventoryModule />} />
              <Route path="reports" element={<ReportsModule />} />
              <Route path="notifications" element={<NotificationsModule />} />
              <Route path="settings" element={<SettingsModule />} />
              <Route path="content" element={<SiteContentModule />} />
              <Route path="security" element={<SecurityModule />} />
              <Route path="system" element={<SystemModule />} />
              <Route path="conditions" element={<ConditionsModule />} />
              <Route path="policies" element={<PoliciesModule />} />
              <Route path="overdue-notices" element={<OverdueNoticesModule />} />
              <Route path="reviews" element={<ReviewsModule />} />
            </Routes>
          </main>
        </div>
        <Toaster />
      </div>
    </DensityProvider>
  );
};

export default AdminDashboard;
